import { action, computed, observable, makeObservable } from 'mobx';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import SafeMath from 'common/safeMath';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import {
    AmountPayType
} from 'nomst/Stores/Configuration/Constants/amountPayType';
import {
    PercentageCalculationRule
} from 'nomst/Stores/Configuration/Constants/percentageCalculationRule';
import { AmountCode } from 'typings/enums';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import * as debounce from 'javascript-debounce';
import { IAmount } from 'typings/amount';
import AmountConvert from 'common/amountConvert';
import { cycleRules } from 'utils/MobxDataManager/singletons';

const amountCodeTitleMap: Map<AmountCode, string> = new Map<AmountCode, string>([
    [AmountCode.Merit, 'Increase'],
    [AmountCode.Bonus, 'Bonus'],
    [AmountCode.LumpSum, 'Lump Sum'],
    [AmountCode.MarketAdjustment, 'Market Adj'],
    [AmountCode.Promotion, 'Promotion'],
    [AmountCode.Stock, 'Stock'],
    [AmountCode.VariablePay, 'Variable Pay']
]);

export class Amount implements IAmount {
    private _amountCode: AmountCode;
    private _payType: Nullable<AmountPayType>;
    private _amountCycleId: number;
    private _budgetCycleId: Nullable<number>;
    private _employeeSourceId: string;
    @observable
    private _isEligible: boolean;
    private _initialIsEligible: boolean;
    private _initialIsProrationOverride: boolean;
    private _isEligibleOverride: Nullable<boolean>;
    private _currentPay: number;
    private _prorateRatio: number;
    private _targetAmount: number;
    @observable private _isProrationOverride: boolean;
    private _percentageCalculationRule: PercentageCalculationRule;
    private _amountIsoCurrencyCode: string;
    private _currencyConversion: CurrencyConversion;
    private _updateNewPay: boolean;
    private _updateTcc: boolean;
    @observable private _overallMultiFieldBonusAmount: number;

    private readonly _decisionDispatcher: Nullable<DecisionDispatcher>;
    @observable private _bonusFieldDecisions: IBonusFieldRule[];

    @observable private _value: number;
    @observable private _currentIsoCurrencyCode: string;

    // tslint:disable:parameters-max-number
    constructor(
        amountDto: IAmount,
        employeeSourceId: string,
        currentPay: number,
        targetAmount: Nullable<number>,
        percentageCalculationRule: Nullable<PercentageCalculationRule>,
        payType: Nullable<AmountPayType>,
        amountIsoCurrencyCode: Nullable<string>,
        currentIsoCurrencyCode: Nullable<string>,
        currencyConversion: CurrencyConversion,
        decisionDispatcher: Nullable<DecisionDispatcher>,
        bonusFieldDecisions: IBonusFieldRule[] = []) {
        makeObservable(this);
        this._amountCode = amountDto.amountCode;
        this._payType = payType;
        this._amountCycleId = amountDto.amountCycleId;
        this._budgetCycleId = amountDto.budgetCycleId;
        this._employeeSourceId = employeeSourceId;
        this._isEligible = amountDto.isEligible;
        this._initialIsEligible = amountDto.isEligible;
        this._isEligibleOverride = amountDto.isEligibleOverride ?? null;
        this._value = amountDto.value ?? 0;
        this._prorateRatio = amountDto.prorateRatio && amountDto.prorateRatio > 0 ? amountDto.prorateRatio : 1;
        this._isProrationOverride = !!amountDto.isProrationOverride;
        this._initialIsProrationOverride = !!amountDto.isProrationOverride;
        this._currentPay = currentPay;
        this._targetAmount = targetAmount || 0;
        this._percentageCalculationRule = percentageCalculationRule ?? PercentageCalculationRule.PercentageOfCurrentPay;
        this._updateNewPay = payType === AmountPayType.BasePay;
        this._updateTcc = !!amountDto.updateTcc;
        this._amountIsoCurrencyCode = amountIsoCurrencyCode ?? 'USD';
        this._currentIsoCurrencyCode = currentIsoCurrencyCode ?? 'USD';
        this._currencyConversion = Coalesce.singleValue(currencyConversion, () => new CurrencyConversion(null));
        this._decisionDispatcher = decisionDispatcher;
        this._bonusFieldDecisions = bonusFieldDecisions;

        if (this._decisionDispatcher) {
            this.debounceAmountChanged = debounce(this.debounceAmountChanged.bind(this), 500);
        }
        if (this._bonusFieldDecisions) {
            this.calculateMultiBonusAmount();
        }
    }

    @action
    setValue(value: number): void {
        this._value = this._currencyConversion.convertCurrency(
            this._currentIsoCurrencyCode,
            this._amountIsoCurrencyCode,
            value);

        this.debounceAmountChanged();
    }

    @action
    setPercentage(percentage: number, sendEvent: boolean = true, annualAmount: Nullable<number> = null): void {
        let calculatedAmount: number;
        if (this._percentageCalculationRule === PercentageCalculationRule.PercentageOfTarget) {
            calculatedAmount = percentage * this._targetAmount * this._prorateRatio / 100;
        } else {
            const currentPay = annualAmount !== null ? annualAmount : this._currentPay;
            calculatedAmount = percentage * currentPay * this._prorateRatio / 100;
        }

        this._value = calculatedAmount;
        if (sendEvent) {
            this.debounceAmountChanged();
        }
    }

    @action
    setCurrentCurrency(currentIsoCurrencyCode: string): void {
        if (!currentIsoCurrencyCode) {
            this._currentIsoCurrencyCode = this._amountIsoCurrencyCode;
            return;
        }

        this._currentIsoCurrencyCode = currentIsoCurrencyCode;
    }

    @computed get value(): number {
        return this._value;
    }

    @computed get prorateRatio(): number {
        return this._prorateRatio;
    }

    @computed
    get convertedAmount(): number {
        return this._currencyConversion.convertCurrency(
            this._amountIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._value);
    }

    @computed
    get percentage(): number {
        let percentageWithoutProrate: number;
        if (this._percentageCalculationRule === PercentageCalculationRule.PercentageOfTarget) {
            percentageWithoutProrate = SafeMath.divide(this._value, this._targetAmount) * 100;
        } else {
            percentageWithoutProrate = SafeMath.divide(this._value, this._currentPay) * 100;
        }

        return SafeMath.divide(percentageWithoutProrate, this._prorateRatio);
    }

    @computed
    get proratedPercentage(): number {
        return this.percentage * this._prorateRatio;
    }

    @computed
    get amountForChangeHistoryDisplay(): number {
        if (this.isProrated && !this.isProrationOverride) {
            return SafeMath.divide(this.value, this._prorateRatio);
        }

        return this.value;
    }

    @computed
    get proratedIncrease(): number {
        return SafeMath.divide(this.value, this._prorateRatio);
    }

    @computed
    get convertedCurrentPay(): number {
        return this._currencyConversion.convertCurrency(
            this._amountIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._currentPay);
    }

    @computed
    get convertedTarget(): number {
        return this._currencyConversion.convertCurrency(
            this._amountIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._targetAmount);
    }

    @computed
    get targetPercentage(): number {
        return SafeMath.divide(this._targetAmount, this._currentPay) * 100;
    }

    @computed
    get isInBounds(): boolean {
        if (this._amountCode !== AmountCode.Merit) {
            return true;
        }

        return cycleRules.isAmountInBounds(this.percentage, this.targetPercentage);
    }

    get amountCode(): AmountCode {
        return this._amountCode;
    }

    get title(): Nullable<string> {
        return amountCodeTitleMap.get(this._amountCode) || null;
    }

    get payType(): Nullable<AmountPayType> {
        return this._payType;
    }

    get amountCycleId(): number {
        return this._amountCycleId;
    }

    get budgetCycleId(): Nullable<number> {
        return this._budgetCycleId;
    }

    @computed
    get isEligible(): boolean {
        return this._isEligible;
    }

    get initialIsEligible(): boolean {
        return this._initialIsEligible;
    }

    get isEligibleOverride(): Nullable<boolean> {
        return this._isEligibleOverride;
    }

    get initialIsProrationOverride(): boolean {
        return this._initialIsProrationOverride;
    }

    get currentPay(): number {
        return this._currentPay;
    }

    get targetAmount(): Nullable<number> {
        return this._targetAmount;
    }

    get updateNewPay(): boolean {
        return this._updateNewPay;
    }

    get updateTcc(): boolean {
        return this._updateTcc;
    }

    get amountIsoCurrencyCode(): string {
        return this._amountIsoCurrencyCode;
    }

    get currentIsoCurrencyCode(): string {
        return this._currentIsoCurrencyCode;
    }

    @computed
    get isProrated(): boolean {
        return this._isProrationOverride === false && this._prorateRatio < 1;
    }

    @computed
    get isProrationOverride(): boolean {
        return this._isProrationOverride;
    }

    get canUserModifyPercentage(): boolean {
        return this._percentageCalculationRule === PercentageCalculationRule.PercentageOfCurrentPay
            || this._targetAmount > 0;
    }

    get bonusFieldDecisions(): IBonusFieldRule[] {
        return this._bonusFieldDecisions;
    }

    @computed
    get overallMultiBonusAmount(): number {
        return this._overallMultiFieldBonusAmount;
    }

    getPercentageBasedOnAnnualPay(annualPay: number): number {
        let percentageWithoutProrate: number;
        if (this._percentageCalculationRule === PercentageCalculationRule.PercentageOfTarget) {
            percentageWithoutProrate = SafeMath.divide(this._value, this._targetAmount) * 100;
        } else {
            percentageWithoutProrate = SafeMath.divide(this._value, annualPay) * 100;
        }

        return SafeMath.divide(percentageWithoutProrate, this._prorateRatio);
    }

    getTargetPercentageAsDecimal(annualStandardWorkHours: number, isHourly: boolean, fullTimeEquivalent: number): number {
        const annualCurrentPay = AmountConvert.convertToAnnual(
            this.convertedCurrentPay,
            annualStandardWorkHours,
            isHourly,
            fullTimeEquivalent);
        return SafeMath.divide(this.convertedTarget, annualCurrentPay);
    }

    @action
    calculateMultiBonusAmount(): void {
        this._overallMultiFieldBonusAmount =
            this._bonusFieldDecisions.reduce((accumulator: number, rule: IBonusFieldRule): number => {
                return accumulator + this.getBonusFactorAmount(rule);
            }, 0);
    }

    @action
    overrideEligibility(eligibility: boolean): void {
        this._isEligible = eligibility;
    }

    @action
    overrideInitialEligibility(eligibility: boolean): void {
        this._initialIsEligible = eligibility;
    }

    @action
    overrideProration(proration: boolean): void {
        this._isProrationOverride = proration;
    }

    @action
    resetInitialIsEligible(): void {
        this._initialIsEligible = this._isEligible;
    }

    @action
    resetInitialIsProration(): void {
        this._initialIsProrationOverride = this._isProrationOverride;
    }

    @action
    resetEligibleOverride(): void {
        this._isEligible = this._initialIsEligible;
    }

    @action
    resetProrationOverride(): void {
        this._isProrationOverride = this._initialIsProrationOverride;
    }

    getBonusFactorAmount(rule: IBonusFieldRule): number {
        const percentage = rule.split / 100 * rule.attained / 100;
        let calculatedAmount: number;
        if (this._percentageCalculationRule === PercentageCalculationRule.PercentageOfTarget) {
            calculatedAmount = percentage * this._targetAmount * this._prorateRatio;
        } else {
            calculatedAmount = percentage * this._currentPay * this._prorateRatio;
        }

        return calculatedAmount;
    }

    @action
    setSplit(value: number, rule: IBonusFieldRule): void {
        rule.split = value;
        this.calculateMultiBonusAmount();
    }

    @action
    setAttained(value: number, rule: IBonusFieldRule): void {
        rule.attained = value;
        this.calculateMultiBonusAmount();
    }

    // for testing
    @action
    overrideBudgetCycleId(id: number): void {
        this._budgetCycleId = id;
    }

    private debounceAmountChanged(): void {
        if (!this._decisionDispatcher) {
            return;
        }
        this._decisionDispatcher.dispatchDebounceAmountChanged(
            this._employeeSourceId,
            this._amountCode,
            this._amountCycleId,
            this._value);
    }
}
